import * as React from "react";
import { MenuTrigger } from "@wellsky/atlas-ui-react";
import type { MenuItemData } from "@wellsky/atlas-ui-react";
import { PortalContext } from "../../widget/WidgetProvider";

// Re-export MenuItemData type for consumers
export type { MenuItemData };

interface DropdownMenuProps {
  children: React.ReactNode;
}

function DropdownMenu({ children }: DropdownMenuProps) {
  const portalContainer = React.useContext(PortalContext);
  return <DropdownMenuCollector portalContainer={portalContainer}>{children}</DropdownMenuCollector>;
}

function DropdownMenuCollector({
  children,
  portalContainer,
}: {
  children: React.ReactNode;
  portalContainer: ShadowRoot | HTMLElement | null;
}) {
  const itemsRef = React.useRef<{ id: string; label: string; icon?: React.ReactNode; disabled?: boolean; onSelect?: () => void }[]>([]);
  const triggerElementRef = React.useRef<React.ReactElement | null>(null);
  const triggerDomRef = React.useRef<HTMLElement | null>(null);
  const [open, setOpen] = React.useState(false);

  // Reset on each render
  itemsRef.current = [];
  triggerElementRef.current = null;

  // Walk children to extract trigger, content, and items
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const type = child.type as React.ComponentType;

    if (type === DropdownMenuTrigger) {
      triggerElementRef.current = child.props.children as React.ReactElement;
    } else if (type === DropdownMenuContent) {
      React.Children.forEach(child.props.children, (itemChild: React.ReactNode) => {
        if (!React.isValidElement(itemChild)) return;
        const itemType = itemChild.type as React.ComponentType;

        if (itemType === DropdownMenuItem) {
          const id = `item-${itemsRef.current.length}`;
          let label = "";
          const icons: React.ReactNode[] = [];
          React.Children.forEach(itemChild.props.children, (c: React.ReactNode) => {
            if (typeof c === "string") {
              label += c;
            } else if (React.isValidElement(c) && typeof c.type !== "string") {
              icons.push(c);
            }
          });
          itemsRef.current.push({
            id,
            label: label.trim(),
            icon: icons.length > 0 ? icons[0] : undefined,
            disabled: itemChild.props.disabled,
            onSelect: itemChild.props.onSelect,
          });
        }
      });
    }
  });

  const items: MenuItemData[] = itemsRef.current.map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    disabled: item.disabled,
  }));

  const selectHandlers = itemsRef.current;

  const handleSelect = (id: string) => {
    const item = selectHandlers.find((i) => i.id === id);
    item?.onSelect?.();
  };

  // Outside-click detection using composedPath() so it works inside Shadow DOM.
  // atlas-ui-react MenuTrigger's built-in handler relies on e.target which gets
  // retargeted to the shadow host, causing mousedown anywhere (including on a
  // menu item) to close the menu before click can fire — see NETSOL-11402.
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const path = e.composedPath();
      const triggerEl = triggerDomRef.current;
      const insideTrigger = triggerEl ? path.includes(triggerEl) : false;
      const insideMenu = path.some(
        (node) => node instanceof Element && node.getAttribute("role") === "menu",
      );
      if (!insideTrigger && !insideMenu) setOpen(false);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  if (!triggerElementRef.current || items.length === 0) {
    return null;
  }

  // Capture the trigger DOM node so composedPath checks can identify it.
  const wrappedTrigger = React.cloneElement(triggerElementRef.current, {
    ref: (el: HTMLElement | null) => {
      triggerDomRef.current = el;
      const originalRef = (triggerElementRef.current as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof originalRef === "function") originalRef(el);
      else if (originalRef && typeof originalRef === "object") {
        (originalRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }
    },
  });

  return (
    <MenuTrigger
      items={items}
      onSelect={handleSelect}
      container={portalContainer as HTMLElement | undefined}
      placement="bottom-start"
      open={open}
      onOpenChange={setOpen}
      closeOnOutsideClick={false}
    >
      {wrappedTrigger}
    </MenuTrigger>
  );
}

function DropdownMenuTrigger(_props: {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  return null;
}

function DropdownMenuContent(_props: {
  children: React.ReactNode;
  className?: string;
  sideOffset?: number;
  align?: string;
}) {
  return null;
}

function DropdownMenuGroup({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function DropdownMenuItem(_props: {
  children: React.ReactNode;
  className?: string;
  inset?: boolean;
  variant?: "default" | "destructive";
  onSelect?: () => void;
  disabled?: boolean;
}) {
  return null;
}

function DropdownMenuCheckboxItem(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuRadioGroup(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuRadioItem(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuLabel(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuSeparator(_props: { className?: string }) {
  return null;
}

function DropdownMenuShortcut(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuSub(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuSubTrigger(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuSubContent(_props: { children?: React.ReactNode }) {
  return null;
}

function DropdownMenuPortal({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
